import { IPropertyEditable, IPropertyPresentable } from "../../../../base/domain/system/edit";
import { TextAlignKinds } from "../../../../base/domain/drawing";
import {
  IPrintBehavior,
  IEditBehavior,
  IInputMethodBehavior,
  InputMethodKinds,
  IPageExtendBehavior,
  ITurnedTextStyle,
  ITableIndex,
  ITableSize,
  ITableScriptExecuteBehavior,
  ILinkedObjectSpecifiable,
  TranscribeTargetKinds,
} from "../../../../documaker/domain/format/objects";
import { IFormatObjectEditable } from "../IFormatObjectEditable";
import { IFormatSpecification } from "../IFormatSpecification";
import { IScriptObject } from "../objects";
import {
  IPrintBehaviorPropertyEditable,
  IEditBehaviorPropertyEditable,
  IInputMethodBehaviorPropertyEditable,
  IPageExtendBehaviorPropertyEditable,
  ITurnedTextStylePropertyEditable,
  ITableSizePropertyEditable,
  ITableScriptExecuteBehaviorPropertyEditable,
} from "../properties";
import { AbstractStandardTextDrawableObjectEditor } from "./AbstractStandardTextDrawableObjectEditor";

export class ScriptObjectEditor extends AbstractStandardTextDrawableObjectEditor<IScriptObject> implements
IPrintBehaviorPropertyEditable,
IEditBehaviorPropertyEditable,
IInputMethodBehaviorPropertyEditable,
IPageExtendBehaviorPropertyEditable,
ITurnedTextStylePropertyEditable,
ITableSizePropertyEditable,
ITableScriptExecuteBehaviorPropertyEditable {
  constructor(source: IScriptObject | ScriptObjectEditor, specification?: IFormatSpecification, isNew: boolean = false) {
    super(source, specification, isNew);
    this._name = this.createNamePropertyEditor(s => false);
    this._printBehavior = this.createPropertyEditor(s => s.printBehavior, (s, v) => s.printBehavior = v);
    this._editBehavior = this.createPropertyEditor(s => s.editBehavior, (s, v) => s.editBehavior = v);
    this._multiLineBehavior = this.createMultiLineBehaviorPropertyEditor();
    this._inputRuleBehavior = this.createInputRuleBehaviorPropertyEditor();
    this._inputMethodBehavior = this.createPropertyEditor(s => s.inputMethodBehavior, (s, v) => s.inputMethodBehavior = v);
    this._pageExtendBehavior = this.createPropertyEditor(s => s.pageExtendBehavior, (s, v) => s.pageExtendBehavior = v);
    this._turnedTextStyle = this.createPropertyEditor(s => s.turnedTextStyle, (s, v) => s.turnedTextStyle = v);
    this._tableIndex = this.createPropertyPresenter(s => s.scriptTableBehavior.index);
    this._tableSize = this.createPropertyEditor(
      s => s.scriptTableBehavior.size,
      (s, v) => s.scriptTableBehavior.size = v,
      s => (s.scriptTableBehavior.index.row === 0) && (s.scriptTableBehavior.index.column === 0));
    this._tableScriptExecuteBehavior = this.createPropertyEditor(
      s => s.scriptTableBehavior.scriptExecuteBehavior,
      (s, v) => s.scriptTableBehavior.scriptExecuteBehavior = v,
      s => (s.scriptTableBehavior.index.row === 0) && (s.scriptTableBehavior.index.column === 0));
    this._scriptGroupKey = this.createNamePropertyEditor();
    this._scriptExecuteTriggerBehavior = this.createScriptExecuteTriggerBehaviorPropertyEditor(s => true);
    this._emptyAlertAllowBehavior = this.createEmptyAlertAllowBehaviorPropertyEditor(s => true);
    this._groupAlertAllowBehavior = this.createGroupAlertAllowBehaviorPropertyEditor(s => true);
    // 仕様では設定できるようになってるがv1は設定できないので無効化.TODO:QA確認後正式に対応する事.
    this._textSearchBehavior = this.createPropertyEditor(s => s.textSearchBehavior, (s, v) => s.textSearchBehavior = v, s => false);
  }

  protected _printBehavior: IPropertyEditable<IPrintBehavior>;
  protected _editBehavior: IPropertyEditable<IEditBehavior>;
  protected _inputMethodBehavior: IPropertyEditable<IInputMethodBehavior>;
  protected _pageExtendBehavior: IPropertyEditable<IPageExtendBehavior>;
  protected _turnedTextStyle: IPropertyEditable<ITurnedTextStyle>;
  protected _tableIndex: IPropertyPresentable<ITableIndex>;
  protected _tableSize: IPropertyEditable<ITableSize>;
  protected _tableScriptExecuteBehavior: IPropertyEditable<ITableScriptExecuteBehavior>;
  protected _scriptGroupKey: IPropertyEditable<string>;

  printBehavior(): IPropertyEditable<IPrintBehavior> {
    return this._printBehavior;
  }

  editBehavior(): IPropertyEditable<IEditBehavior> {
    return this._editBehavior;
  }

  inputMethodBehavior(): IPropertyEditable<IInputMethodBehavior> {
    return this._inputMethodBehavior;
  }

  getSelectableInputMethods(): InputMethodKinds[] {
    return this._specification.objects.script.inputMethods;
  }

  pageExtendBehavior(): IPropertyEditable<IPageExtendBehavior> {
    return this._pageExtendBehavior;
  }

  turnedTextStyle(): IPropertyEditable<ITurnedTextStyle> {
    return this._turnedTextStyle;
  }

  tableIndex(): IPropertyPresentable<ITableIndex> {
    return this._tableIndex;
  }

  tableSize(): IPropertyEditable<ITableSize> {
    return this._tableSize;
  }

  tableScriptExecuteBehavior(): IPropertyEditable<ITableScriptExecuteBehavior> {
    return this._tableScriptExecuteBehavior;
  }

  scriptGroupKey(): IPropertyEditable<string> {
    return this._scriptGroupKey;
  }

  getTypeName(): string {
    return "外部データ";
  }

  getIdentifiableName(): string {
    return this.name().getValue() + "[" + this.tableIndex().getValue().row + "][" + this.tableIndex().getValue().column + "]";
  }

  toLinkedObjectIdentifier(): ILinkedObjectSpecifiable {
    return {
      type: this.objectTypeName().getValue(),
      id: this.id().getValue(),
      key: this.name().getValue(),
      tableIndex: this.tableIndex().getValue(),
    };
  }

  getSelectableAlignmentKinds(): TextAlignKinds[] {
    return this._specification.objects.script.alignmentKinds;
  }

  getSelectableTranscribeTargets(): TranscribeTargetKinds[] {
    return this._specification.objects.script.transcription.targets;
  }

  clone(): IFormatObjectEditable {
    return new ScriptObjectEditor(this);
  }
}
